import { createTelemetry, createTelemetryObject } from '@shared/core/analytics';
import type { PageFunctionType } from '@shared/core/analytics/telemetry/types';
import type { PayoutAccountInfo } from './CreditCardSetUp.context';
import type { ApolloError } from '@apollo/client';
import type { CashFundBalanceTelemetry } from '@shared/utils/cashFund/cashFund.types';

const pagePrefix = 'admin.registry';

export type PagesForTelemetry = 'creditCardSetupDialog' | 'payoutDialog';

const pages: { [tab in PagesForTelemetry]: PageFunctionType } = {
  creditCardSetupDialog: (status: string) => ({
    category: 'joyCredit',
    pagePrefix,
    page: 'CreditCardOnboardingDialog.' + status,
  }),
  payoutDialog: (status: string) => ({
    category: 'joyCredit',
    pagePrefix,
    page: 'PayoutDialog.' + status,
  }),
};

export const creditCardSetupTelemetry = createTelemetryObject({
  actions: {
    getStartedCTAButton: () => ({
      pagePrefix,
      category: 'track.joyCredit',
      action: 'ButtonInteracted',
      label: 'Get Started',
      extraInfo: {
        name: 'OpenCreditCardOnboardingDialog',
      },
    }),
    toggleCreditCardOption: () => ({
      pagePrefix,
      category: 'addCashFund.paymentMethod',
      action: 'ButtonInteracted',
      label: 'Credit Card Toggle',
      extraInfo: {
        name: 'OpenCreditCardOnboardingDialog',
      },
    }),
    letsGetsStartedButtonClicked: () => ({
      pagePrefix,
      category: 'joyCredit.dialog',
      action: 'ButtonInteracted',
      label: `Let's Get Started`,
      extraInfo: {
        name: 'LaunchStripeOnboarding',
      },
    }),
    transferBalanceButtonClicked: (balance: number, currency: string, fee: number) => ({
      pagePrefix,
      category: 'payout.dialog',
      action: 'ButtonInteracted',
      label: 'Yes, Transfer Balance',
      extraInfo: {
        name: 'TransferBalance',
        balance,
        currency,
        fee,
      },
    }),
    nevermindButtonClicked: () => ({
      pagePrefix,
      category: 'payout.dialog',
      action: 'ButtonInteracted',
      label: 'Nevermind',
      extraInfo: {
        name: 'ClosePayoutDialog',
      },
    }),
    transferBalanceWidgetButtonClicked: () => ({
      pagePrefix,
      category: 'joyCredit.widget',
      action: 'ButtonInteracted',
      label: 'Transfer Balance',
      extraInfo: {
        name: 'OpenPayoutDialog',
      },
    }),
    unlockHoneymoonOffersClicked: (cashFundBalance: CashFundBalanceTelemetry = {}) => ({
      pagePrefix,
      category: 'joyCredit.widget',
      action: 'ButtonInteracted',
      label: 'Unlock Honeymoon Offers',
      // Every promo CTA now opens the honeymoon landing page, so the next funnel step is that
      // page's own view event, split by `honeymoon_entry_source`.
      extraInfo: {
        name: 'OpenCashFundHotelSearch',
        ...cashFundBalance,
        honeymoon_cta_target: 'landing',
      },
    }),
    honeymoonOffersPromoViewed: (cashFundBalance: CashFundBalanceTelemetry = {}) => ({
      pagePrefix,
      category: 'joyCredit.widget',
      action: 'PageViewed',
      label: 'Honeymoon Offers Promo',
      extraInfo: {
        name: 'JoyCreditWidget.HoneymoonOffersPromo',
        ...cashFundBalance,
      },
    }),
    accountButtonClicked: (url: string) => ({
      pagePrefix,
      category: 'joyCredit.widget',
      action: 'ButtonInteracted',
      label: 'Account',
      extraInfo: {
        name: 'OpenCustomerStripeConnectDashboard',
        url,
      },
    }),
    closePayoutDialogStatusButtonClicked: (status: string) => ({
      pagePrefix,
      category: 'joyCredit.widget',
      action: 'ButtonInteracted',
      label: 'Close',
      extraInfo: {
        name: 'ClosePayoutTransferStatusDialog',
        status,
      },
    }),
    resolveStripeIssueButtonClicked: (category: string, url: string) => ({
      pagePrefix,
      category,
      action: 'ButtonInteracted',
      label: 'Resolve Stripe Issue',
      extraInfo: {
        name: 'ResolveStripeIssue',
        url,
      },
    }),
    checkStripeStatusClicked: (category: string, url) => ({
      pagePrefix,
      category,
      action: 'ButtonInteracted',
      label: 'Check Stripe Status',
      extraInfo: {
        name: 'CheckStripeStatus',
        url,
      },
    }),
    createCashFundButtonClicked: () => ({
      pagePrefix,
      category: 'joyCredit.dialog',
      action: 'ButtonInteracted',
      label: 'Create Cash Fund',
      extraInfo: {
        name: 'CreateCashFundFromCrediCardOnboardingSuccessDialog',
      },
    }),
    transferToBankButtonClicked: () => ({
      pagePrefix,
      category: 'joyCredit.dialog',
      action: 'ButtonInteracted',
      label: 'Transfer to Bank',
      extraInfo: {
        name: 'TransferToBankFromPayoutMethodAddedDialog',
      },
    }),
    illDoLaterButtonClicked: () => ({
      pagePrefix,
      category: 'joyCredit.dialog',
      action: 'ButtonInteracted',
      label: `I'll do it later`,
      extraInfo: {
        name: 'CloseCrediCardOnboardingSuccessDialog',
      },
    }),
    initHostFormSubmitted: () => ({
      pagePrefix,
      category: 'joyCredit.dialog',
      action: 'ButtonInteracted',
      label: 'Continue',
      extraInfo: {
        name: 'InitHostFormSubmitted',
      },
    }),
    bankAccountBannerCtaClicked: (countryCode: string, currencyCode: string) => ({
      pagePrefix,
      category: 'stripeSetup.banner',
      action: 'ButtonInteracted',
      label: 'Enable card payments',
      extraInfo: {
        countryCode,
        currencyCode,
      },
    }),
    kycContinueButtonClicked: (payoutInfo: PayoutAccountInfo) => ({
      pagePrefix,
      category: 'joyCredit.dialog',
      action: 'ButtonInteracted',
      label: 'Continue',
      extraInfo: {
        payoutInfo,
      },
    }),
    /**
     * The couple clicked through to Stripe's hosted flow to clear a block on their account.
     * `status` is the dimension that matters here: it separates a couple Stripe restricted after
     * they had been transferring from one who never finished onboarding at all.
     */
    stripeRemediationCtaClicked: (payoutInfo: PayoutAccountInfo) => ({
      pagePrefix,
      category: 'joyCredit.widget',
      action: 'ButtonInteracted',
      label: 'Verify your account to transfer',
      extraInfo: {
        name: 'RedirectToStripeRemediation',
        status: payoutInfo.status,
        statusDetails: payoutInfo.statusDetails,
      },
    }),
    stripeAccountMenuOpenClicked: () => ({
      pagePrefix,
      category: 'joyCredit.widget',
      action: 'ButtonInteracted',
      label: 'More options',
      extraInfo: {
        name: 'OpenStripeAccountMenu',
      },
    }),
    manageStripeAccountClicked: () => ({
      pagePrefix,
      category: 'joyCredit.widget',
      action: 'ButtonInteracted',
      label: 'Manage Stripe Account',
      extraInfo: {
        name: 'RedirectToStripeExpressDashboard',
      },
    }),
    unlinkDialogViewed: (dialog: 'confirm' | 'unlinking') => ({
      pagePrefix,
      category: 'joyCredit.widget',
      action: 'PageViewed',
      label: dialog === 'confirm' ? 'Unlink Stripe Account' : 'Unlinking your account',
      extraInfo: {
        name: dialog === 'confirm' ? 'UnlinkConfirmDialog' : 'UnlinkingDialog',
      },
    }),
    unlinkButtonClicked: (cta: 'unlink' | 'confirm' | 'cancel') => ({
      pagePrefix,
      category: 'joyCredit.widget',
      action: 'ButtonInteracted',
      label: { unlink: 'Unlink Stripe Account', confirm: 'Unlink', cancel: 'Cancel' }[cta],
      extraInfo: {
        name: { unlink: 'OpenUnlinkConfirmDialog', confirm: 'StartPayoutAccountUnlink', cancel: 'CancelPayoutAccountUnlink' }[cta],
      },
    }),
    // Error tracking
    trackError: (mutation: string, error: ApolloError | Error, extra = {}) => ({
      category: 'error.joyCredit',
      action: 'Mutation',
      actionType: 'error',
      extraInfo: { error, mutation, ...extra },
    }),
  },
  pages,
});

export const { TelemetryProvider, useTelemetry: useCreditCardSetupTelemetry } = createTelemetry(creditCardSetupTelemetry, { eventId: '' });
